'use client'

import { getProductsNavigation } from './product-category.utils'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { usePathname } from 'next/navigation'
import { use, useCallback, useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

type ProductNavigationChipsProps = {
  navigation: ReturnType<typeof getProductsNavigation>
}

const chipClass =
  'whitespace-nowrap border border-secondary-900 p-2 text-xs text-secondary-900 hover:bg-grey-500'
const activeClass = 'bg-grey-400'

function ProductNavigationChips({ navigation }: ProductNavigationChipsProps) {
  const navItems = use(navigation)
  const path = usePathname()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1)
  }, [])

  useEffect(() => {
    updateScrollState()
    const el = scrollRef.current
    el?.addEventListener('scroll', updateScrollState, { passive: true })
    return () => el?.removeEventListener('scroll', updateScrollState)
  }, [updateScrollState])

  const scroll = (direction: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: direction === 'left' ? -160 : 160, behavior: 'smooth' })
  }

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => scroll('left')}
        aria-label="Scroll left"
        className={cn(
          'flex shrink-0 items-center justify-center transition-opacity',
          canScrollLeft ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <ChevronLeft className="size-4" />
      </button>

      <div
        ref={scrollRef}
        className="flex flex-1 items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {navItems.map((item) => {
          const href = `/product-categories/${item.slug}`
          const hasSubitems = !!item.subitems?.length
          const isActive =
            path === href ||
            (hasSubitems &&
              item.subitems!.some((sub) => path === `/product-categories/${sub.slug}`))

          if (hasSubitems) {
            return (
              <DropdownMenu key={item.name}>
                <DropdownMenuTrigger
                  className={cn(chipClass, 'flex items-center gap-1', isActive && activeClass)}
                >
                  {item.name}
                  <ChevronDown className="size-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[10rem] rounded-none p-0">
                  <DropdownMenuItem
                    asChild
                    className="cursor-pointer rounded-none px-3 py-2 text-xs text-secondary-900 focus:bg-grey-500"
                  >
                    <Link href={href}>All {item.name}</Link>
                  </DropdownMenuItem>
                  {item.subitems!.map((sub) => {
                    const subHref = `/product-categories/${sub.slug}`
                    return (
                      <DropdownMenuItem
                        key={sub.name}
                        asChild
                        className={cn(
                          'cursor-pointer rounded-none px-3 py-2 text-xs text-secondary-900 focus:bg-grey-500',
                          path === subHref && activeClass,
                        )}
                      >
                        <Link href={subHref}>{sub.name}</Link>
                      </DropdownMenuItem>
                    )
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )
          }

          return (
            <Link
              key={item.name}
              href={href}
              className={cn(chipClass, isActive && activeClass)}
            >
              {item.name}
            </Link>
          )
        })}
      </div>

      <button
        onClick={() => scroll('right')}
        aria-label="Scroll right"
        className={cn(
          'flex shrink-0 items-center justify-center transition-opacity',
          canScrollRight ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
  )
}

export default ProductNavigationChips
